import { PH_UNMASK_CLASS } from "@faire/globals/posthog/PH_UNMASK_CLASS";
import { cn } from "@faire/react/tools/cn";
import React from "react";

import { FormFieldValidationStatusIndicator } from "./FormFieldValidationStatusIndicator";
import type { FormFieldValidationStatus } from "./types";

type IProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "prefix" | "sufix"
> & {
  /**
   * The error message of the input component.
   */
  error?: React.ReactNode;

  /**
   * The container ref (when prefix or suffix is provided).
   */
  containerRef?: React.Ref<HTMLDivElement>;

  /**
   * Trailing field status. `validating` shows a spinner and sets `aria-busy`;
   * `validated` shows a check. Omit to hide the status indicator.
   */
  status?: FormFieldValidationStatus;

  /**
   * The prefix of the input component.
   */
  prefix?: React.ReactNode;

  /**
   * The ref of the input component.
   */
  ref?: React.Ref<HTMLInputElement>;

  /**
   * The suffix of the input component.
   */
  suffix?: React.ReactNode;

  /**
   * The function to call when the container is clicked.
   */
  onContainerClick?: React.MouseEventHandler<HTMLDivElement>;

  /**
   * The function to call on mousedown on the container.
   */
  onContainerMouseDown?: React.MouseEventHandler<HTMLDivElement>;

  /**
   * The data component name. Used for adoption tracking.
   */
  "data-component-name"?: string;

  /**
   * When `true`, opts this input into PostHog session replay unmasking so
   * typed values are recorded instead of masked. Only use for inputs that
   * never hold PII.
   */
  unmaskPosthog?: boolean;
};

export const InputBase: React.FC<IProps> = (props) => {
  const {
    className,
    error,
    onContainerClick,
    onContainerMouseDown,
    unmaskPosthog,
    status,
    prefix,
    suffix,
    containerRef,
    "data-component-name": dataComponentName,
    ...rest
  } = props;
  const unmaskClassName = unmaskPosthog ? PH_UNMASK_CLASS : undefined;
  const isValidating = status === "validating";
  const hasAddon = Boolean(prefix || suffix || status);
  const inputValue = typeof rest.value === "string" ? rest.value : undefined;
  // NOTE: input cannot be rendered in 2 separate branches as that will result in it being unmounted & remounted (causing loss of focus) whenever suffix and/or prefix changes dynamically while user is typing (e.g. TextInput with status)
  return (
    <Container {...props}>
      {prefix ? (
        <span className="fs-form-field__prefix flex items-center">
          {prefix}
        </span>
      ) : null}
      <input
        {...rest}
        aria-busy={isValidating || undefined}
        aria-invalid={!!error}
        {...(!hasAddon
          ? {
              className: cn("fs-form-field", className, unmaskClassName),
              "data-component-name": dataComponentName,
            }
          : {
              className: cn(
                "w-full focus-visible:outline-none",
                unmaskClassName
              ),
            })}
      />
      {suffix ? (
        <span className="fs-form-field__suffix flex items-center">
          {suffix}
        </span>
      ) : null}
      {status ? (
        <FormFieldValidationStatusIndicator
          status={status}
          value={inputValue}
        />
      ) : null}
    </Container>
  );
};

const Container: React.FC<React.PropsWithChildren<IProps>> = ({
  prefix,
  suffix,
  status,
  children,
  className,
  "data-component-name": dataComponentName,
  onContainerClick,
  onContainerMouseDown,
  containerRef,
}) => {
  const containerProps = {
    onClick: onContainerClick,
    onMouseDown: onContainerMouseDown,
    ref: containerRef,
  };
  if (!prefix && !suffix && !status) {
    // NOTE: both logic branches must have children wrapped in the same parent so that React doesn't unmount and remount the children when the logic branch changes
    return <div {...containerProps}>{children}</div>;
  }
  return (
    <div
      {...containerProps}
      className={cn(
        "fs-form-field flex items-center gap-(--slate-spacing-element-default)",
        className
      )}
      data-component-name={dataComponentName}
    >
      {children}
    </div>
  );
};
